/**
 * GloomBat.ts
 * Swooping flying enemy for Starfall Courier.
 *
 * Hovers at a fixed Y position tracking Nova horizontally. When Nova enters
 * detection range, GloomBat enters a 0.5s TELEGRAPH phase (visible warning)
 * then dives in a straight line toward Nova's last position. Resets to hover
 * after leaving the screen or after the dive completes.
 *
 * State machine: HOVER → TELEGRAPH → DIVING → RECOVERING → HOVER → …
 *
 * Killable by stomp or Star Shard.
 *
 * Rules (AGENTS.md): No React. No async. No Skia. Extends BaseEntity.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 *
 * @module GloomBat
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import Logger from '../../utils/logger';

export type GloomBatState = 'HOVER' | 'TELEGRAPH' | 'DIVING' | 'RECOVERING' | 'DEAD';

export const GLOOM_BAT_WIDTH = 28;
export const GLOOM_BAT_HEIGHT = 20;

/** Horizontal tracking speed while hovering (px/s). */
const HOVER_SPEED = 50;
/** Detection range — horizontal distance from Nova that triggers telegraph (px). */
const DETECT_RANGE = 180;
/** Max distance from spawn that the bat will track horizontally (px). */
const PATROL_RANGE = 200;
/** Duration of the telegraph flash before diving (seconds). */
const TELEGRAPH_DURATION = 0.5;
/** Dive speed (px/s). */
const DIVE_SPEED = 280;
/** How long the bat recovers (floats back up) after a dive (seconds). */
const RECOVER_DURATION = 1.0;
/** Upward speed during recovery (px/s). */
const RECOVER_SPEED = 80;
/** Post-dive cooldown before the bat can detect Nova again (seconds). */
const DIVE_COOLDOWN = 2.0;

export const GLOOM_BAT_COLOR = '#3a5a8c'; // dark blue
export const GLOOM_BAT_TELEGRAPH_COLOR = '#e8e040'; // yellow flash

/**
 * GloomBat — swooping flying enemy.
 *
 * Constructed with spawn position. The Y passed to the constructor becomes the
 * hover altitude. Pass `nova` to `update()` each tick.
 */
export class GloomBat extends BaseEntity {
  public readonly entityType = 'GloomBat';
  /** Greybox fill colour — returns telegraph color during TELEGRAPH state. */
  public get color(): string {
    return this.telegraphing ? GLOOM_BAT_TELEGRAPH_COLOR : GLOOM_BAT_COLOR;
  }
  public state: GloomBatState = 'HOVER';

  /**
   * True once dead. GloomBat has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** True during TELEGRAPH — renderer uses this to draw the warning flash. */
  public telegraphing = false;
  /** Controls which way the sprite faces. GloomBat sprites natively face left. */
  public facingRight = false;
  private _timer = 0;
  private _hoverY: number;
  /** Spawn X — bat patrols within PATROL_RANGE of this position. */
  private _spawnX: number;
  /** Cooldown timer after recovering — prevents immediate re-dives. */
  private _cooldown = 0;
  /** X at dive start — used to measure total horizontal travel. */
  private _diveStartX = 0;
  /** Dive direction (normalised). */
  private _diveVx = 0;
  private _diveVy = 0;
  /** Accumulated time for smooth hover bob oscillation. */
  private _hoverTime = 0;
  /** Peak-to-peak hover bob amplitude in pixels. */
  private static readonly _BOB_AMP = 3;

  constructor(x: number, y: number) {
    super(x, y, GLOOM_BAT_WIDTH, GLOOM_BAT_HEIGHT);
    this._hoverY = y;
    this._spawnX = x;
  }

  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    switch (this.state) {
      case 'HOVER':
        this._updateHover(dt, nova);
        break;
      case 'TELEGRAPH':
        this._updateTelegraph(dt, nova);
        break;
      case 'DIVING':
        this._updateDiving(dt, level);
        break;
      case 'RECOVERING':
        this._updateRecovering(dt);
        break;
    }
  }

  private _updateHover(dt: number, nova?: NovaRuntime): void {
    if (!nova) return;

    // DECISION: Tick down cooldown during hover. The bat won't detect or
    // track Nova until cooldown expires, preventing rapid re-dives.
    if (this._cooldown > 0) {
      this._cooldown -= dt;
      // Drift back toward spawn during cooldown instead of tracking Nova
      const toSpawn = this._spawnX - this.x;
      if (toSpawn !== 0) this.facingRight = toSpawn > 0;
      this.x += Math.sign(toSpawn) * Math.min(HOVER_SPEED * dt, Math.abs(toSpawn));
      this._hoverTime += dt;
      this.y = this._hoverY + Math.sin(this._hoverTime * 3) * GloomBat._BOB_AMP;
      return;
    }

    // DECISION: Patrol range limits horizontal tracking so bats from
    // earlier zones don't follow the player across the entire level.
    const distFromSpawn = Math.abs(this.x - this._spawnX);
    const dx = nova.x - this.x;
    if (dx !== 0) this.facingRight = dx > 0;

    if (distFromSpawn < PATROL_RANGE || Math.sign(dx) !== Math.sign(this.x - this._spawnX)) {
      this.x += Math.sign(dx) * Math.min(HOVER_SPEED * dt, Math.abs(dx));
    } else {
      // Beyond patrol range and Nova is even further — drift back to spawn
      const toSpawn = this._spawnX - this.x;
      this.x += Math.sign(toSpawn) * Math.min(HOVER_SPEED * dt, Math.abs(toSpawn));
    }
    // DECISION: Sinusoidal bob around _hoverY makes flight look organic.
    this._hoverTime += dt;
    this.y = this._hoverY + Math.sin(this._hoverTime * 3) * GloomBat._BOB_AMP;

    // Trigger telegraph when Nova is close enough AND within patrol range
    if (Math.abs(dx) <= DETECT_RANGE && distFromSpawn <= PATROL_RANGE) {
      this.state = 'TELEGRAPH';
      this.telegraphing = true;
      this._timer = TELEGRAPH_DURATION;
      Logger.debug('GloomBat', 'Telegraph');
    }
  }

  private _updateTelegraph(dt: number, nova?: NovaRuntime): void {
    this._timer -= dt;
    if (this._timer <= 0 && nova) {
      // Commit dive direction toward Nova's current position
      const dx = nova.x - this.x;
      const dy = nova.y - this.y;
      const mag = Math.sqrt(dx * dx + dy * dy) || 1;
      this._diveVx = (dx / mag) * DIVE_SPEED;
      this._diveVy = (dy / mag) * DIVE_SPEED;
      this._diveStartX = this.x;
      this.state = 'DIVING';
      this.telegraphing = false;
      Logger.debug('GloomBat', 'Dive start');
    }
  }

  private _updateDiving(dt: number, level: LevelRuntime): void {
    this.x += this._diveVx * dt;
    this.y += this._diveVy * dt;

    // DECISION: Clamp dive at ground level. Check if the bat's bottom edge
    // would overlap any solid platform — if so, stop at the platform surface
    // and begin recovery. This prevents the bat from flying through dirt.
    for (const p of level.platforms) {
      if (
        this.x + this.width > p.x &&
        this.x < p.x + p.width &&
        this.y + this.height > p.y &&
        this.y < p.y + p.height
      ) {
        this.y = p.y - this.height;
        this.state = 'RECOVERING';
        this._timer = RECOVER_DURATION;
        Logger.debug('GloomBat', 'Dive hit ground, recovering');
        return;
      }
    }

    // Recover after travelling past the hover altitude downward, moving out of
    // bounds, or after covering enough horizontal distance (prevents infinite
    // horizontal flight on shallow dives when Nova is near hover altitude).
    const travelledX = Math.abs(this.x - this._diveStartX);
    if (this.y > this._hoverY + 200 || this.y < -100 || travelledX > 400) {
      this.state = 'RECOVERING';
      this._timer = RECOVER_DURATION;
      Logger.debug('GloomBat', 'Dive end, recovering');
    }
  }

  private _updateRecovering(dt: number): void {
    // DECISION: Return to BOTH hoverY (vertical) AND spawnX (horizontal)
    // during recovery. Without horizontal return, repeated dive cycles
    // let bats drift across the level toward the player — causing the
    // "swarmed by 3 birds" problem at the start of the level.
    const dy = this._hoverY - this.y;
    const dxToSpawn = this._spawnX - this.x;

    // Vertical return
    if (Math.abs(dy) < RECOVER_SPEED * dt) {
      this.y = this._hoverY;
    } else {
      this.y += Math.sign(dy) * RECOVER_SPEED * dt;
    }

    // Horizontal return — move toward spawn at same speed
    if (dxToSpawn !== 0) this.facingRight = dxToSpawn > 0;

    if (Math.abs(dxToSpawn) < RECOVER_SPEED * dt) {
      this.x = this._spawnX;
    } else {
      this.x += Math.sign(dxToSpawn) * RECOVER_SPEED * dt;
    }

    // Done when both axes are home
    if (this.y === this._hoverY && Math.abs(this.x - this._spawnX) < 1) {
      this.x = this._spawnX;
      this._cooldown = DIVE_COOLDOWN;
      this.state = 'HOVER';
      Logger.debug('GloomBat', 'Back to hover (cooldown)');
    }
  }

  /**
   * Apply a stomp or Star Shard hit. GloomBat dies in one hit from either.
   * @returns true if the enemy was killed by this hit (false if already dead).
   */
  public applyHit(): boolean {
    if (this.state === 'DEAD') return false;
    this.state = 'DEAD';
    this.dead = true;
    Logger.debug('GloomBat', 'Killed');
    return true;
  }
}
